import React, { ReactElement, useState, useEffect } from 'react'
import Selector, { SelectorProps } from '../../../components/Selector'
import { Fab, Button, TextField, TextFieldProps, Grid, Tooltip } from '@mui/material'
//import { DatePicker, DateTimePicker, TimePicker } from '@mui/lab'
import { FilterAltOff } from '@mui/icons-material'
import { identity, isEmpty } from 'ramda'
import { useHistory, useLocation } from 'react-router-dom'
import moment, { Moment } from 'moment'
import { paramsToQueryString } from '../../../utils/paramsToQueryString'
import AutocompleteAsyncTextField, { AutocompleteAsyncTextFieldProps } from '../AutocompleteAsyncTextField'
import { DesktopDatePicker } from '@mui/lab'
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import AdapterMoment from '@mui/lab/AdapterMoment'
import { useThemeContext } from '../../../theme/ThemeContext'
import { useTranslation } from 'react-i18next'

export type Filters = { [key: string]: string }

export type FilterElement = ReactElement<FilterProps>

export interface FiltersBarProps {
  defaultFilters?: Filters
  clearFilterLeftPosition?: boolean
  noControlFilters?: boolean
  resetOnChange?: string[]; // Filtro que cuando cambie debe limpiar otros filtros
  clearDependentFilters?: (filters: Filters, name: string) => Filters; // Función para limpiar filtros dependientes
  children?: FilterElement | FilterElement[]
}

export interface FilterProps {
  name: string
  value?: any
  onChange?: (name: string, value: string) => void
  filters?: Filters
  controlledLength?: number
}

interface WeekFilterButtonProps {
  from: Moment
  text: string
}

export const defaultFilters: Filters = {
  from: moment().startOf('year').subtract(1, 'years').tz("America/Argentina/Buenos_Aires").format(),
  to: moment().endOf('month').tz("America/Argentina/Buenos_Aires").format(),
}

export const FiltersBar: React.FC<FiltersBarProps> = ({ children, defaultFilters, clearFilterLeftPosition = true, noControlFilters = false,
  resetOnChange,
  clearDependentFilters,
}) => {
  const history = useHistory()
  const location = useLocation()
  const { t } = useTranslation('others')

  const initFilters = defaultFilters || {}
  const [filters, setFilters] = useState<Filters>(searchToFilters(location.search, initFilters))

  useEffect(() => {
    if (!noControlFilters && isEmpty(location.search) && defaultFilters !== null) {
      submitSearch(filters)

    }
    if (!isEmpty(location.search)) {
      const searchFilters = searchToFilters(location.search)
      setFilters(searchFilters)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilters, location.search])

  const submitSearch = (newFilters: Filters) => {
    history.push({ search: filtersToSearch(newFilters), state: location.state })
  }

  const clearFilters = () => {
    setFilters(initFilters)
    submitSearch(initFilters)
  }

  const onChange = (parentOnChange: (name: string, value: any) => void = identity) => (name: string, value: any) => {

    // Construimos el nuevo objeto de filtros directamente
    const newFilters = { ...filters, [name]: value };

    parentOnChange(name, value)

    // Si se pasa resetOnChange, limpiamos los filtros dependientes
    if (resetOnChange && resetOnChange.includes(name)) {
      const updatedFilters = clearDependentFilters ? clearDependentFilters(newFilters, name) : newFilters;
      submitSearch(updatedFilters); // Llamamos a submitSearch con el nuevo estado
      setFilters(updatedFilters); // Actualizamos el estado solo después de llamar a submitSearch
    } else {
      setFilters(newFilters);
      submitSearch(newFilters); // Llamamos a submitSearch inmediatamente
    }
  };

  if (!children) { return <></> }
  return (<form style={{ margin: 10, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
    {clearFilterLeftPosition &&
      <Tooltip title={t("clearFilter")}>
        <Fab
          color="secondary" size="medium" variant="extended" style={{ marginRight: '10px' }}
          onClick={clearFilters}
        >
          <FilterAltOff />
        </Fab>
      </Tooltip>
    }

    <Grid container>
      {React.Children.map(children, (child: ReactElement<FilterProps>) =>
        <Grid item style={{ display: 'flex', alignItems: 'center', marginRight: 2 }} >
          {React.cloneElement<FilterProps>(child, {
            filters: filters,
            onChange: onChange(child.props.onChange)
          })}
        </Grid>
      )}
    </Grid>

    {!clearFilterLeftPosition &&
      <Tooltip title={t("clearFilter")}>
        <Fab
          color="secondary" size="medium" variant="extended"
          onClick={clearFilters}
        >
          <FilterAltOff />
        </Fab>
      </Tooltip>
    }
  </form >)
}

export const FilterAutocompleteField = <T extends {}>(props: Omit<AutocompleteAsyncTextFieldProps<T>, 'onSelect'> & FilterProps) =>
  <AutocompleteAsyncTextField<T>
    {...props}
    search={props.search}
    inputValue={props.value || (props.filters && props.filters[props.name]) || null}
    //onSelect={selected => selected && props.onChange && props.onChange(props.name, selected![props.name])}
    onSelect={selected => { props.name && props.onChange && props.onChange(props.name, selected ? selected![props.name] : '') }}
  />

export const FilterTextField: React.FC<Omit<TextFieldProps, keyof FilterProps> & FilterProps> = (props) => {
  return (<TextField
    {...props}
    style={{ maxWidth: "200px", minWidth: "80px", flexGrow: 1, ...props.style }}
    value={props.value || (props.filters && props.filters[props.name]) || ''}
    onChange={event => props.onChange && props.onChange(props.name, event.target.value)}
  />)
}

export const FilterSelector: React.FC<SelectorProps & FilterProps> = (props) => {
  return (<Selector
    {...props}
    style={{ minWidth: 120, flexGrow: 1, ...props.style }}
    margin="none"
    value={props.value || (props.filters && props.filters[props.name])}
    onChange={event => { props.onChange && props.onChange(props.name, event.target.value) }}
  />)
}

type TimeCorrector = { timeCorrector: (date: Moment) => Moment }  //TODO find better name :D

export const FilterDate: React.FC<FilterProps & TimeCorrector> = ({ timeCorrector, ...props }) => {
  const { theme } = useThemeContext()
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DesktopDatePicker
        {...props}
        inputFormat="DD/MM/yyyy"
        renderInput={(params) => <TextField sx={{ maxWidth: 200, svg: { fill: theme.typography.button.color } }} {...params} />}
        value={props.value || (props.filters && props.filters[props.name]) || null}
        onChange={date => { if (date) { props.onChange && props.onChange(props.name, timeCorrector(date).tz("America/Argentina/Buenos_Aires").format()) } }}
      //ButtonProps={{ 'aria-label': 'change date', }}
      />
    </LocalizationProvider>)
}

export const FilterMonth: React.FC<FilterProps & TimeCorrector> = ({ timeCorrector, ...props }) => {
  const { theme } = useThemeContext()
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DesktopDatePicker
        {...props}
        views={[
          "month",
          "year"
        ]}
        inputFormat="MM/yyyy"
        renderInput={(params) => <TextField sx={{ maxWidth: 200, svg: { fill: theme.typography.button.color } }} {...params} />}
        value={props.value || (props.filters && props.filters[props.name]) || null}
        onChange={date => { if (date) { props.onChange && props.onChange(props.name, timeCorrector(date).tz("America/Argentina/Buenos_Aires").startOf('month').format() ) }} }
      //ButtonProps={{ 'aria-label': 'change date', }}
      />
    </LocalizationProvider>)
}

export const FilterCtrlDate: React.FC<FilterProps & TimeCorrector & { label?: string }> = ({
  timeCorrector,
  label,
  ...props
}) => {
  const { theme } = useThemeContext()

  const value =
    props.value ||
    (props.filters && props.filters[props.name]) ||
    null

  const handleApply = (date: Moment | null) => {
    if (!date) {
      props.onChange && props.onChange(props.name, '')
      return
    }

    if (!moment.isMoment(date) || !date.isValid()) return

    props.onChange &&
      props.onChange(
        props.name,
        timeCorrector(date)
          .tz("America/Argentina/Buenos_Aires")
          .format()
      )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <DesktopDatePicker
        inputFormat="DD/MM/yyyy"
        label={label}
        value={value}
        onChange={() => {
          // NO hacemos nada mientras escribe
        }}
        onAccept={handleApply}
        renderInput={(params) => (
          <TextField
            {...params}
            sx={{ maxWidth: 200, svg: { fill: theme.typography.button.color } }}
          />
        )}
      />
    </LocalizationProvider>
  )
}

/*
export const FilterTime: React.FC<FilterProps> = (props) => {
  return (<TimePicker
    {...props}
    sx={{ maxWidth: 15, flexGrow: 2 } }
    ampm={false}
    value={props.value || (props.filters && props.filters[props.name] && moment(props.filters[props.name], 'HH:mm')) || null}
    onChange={time => { if (time) { props.onChange && props.onChange(props.name, time.tz("America/Argentina/Buenos_Aires").format('HH:mm')) } }}
    //ButtonProps={{ 'aria-label': 'Hora de inicio' }}
    //Icon={<AccessTime />}
  />)
}
*/

export const WeekFilterButton: React.FC<WeekFilterButtonProps> = (props) => {

  const history = useHistory()
  const location = useLocation()

  const changeDateInterval = (from: Moment) => {
    const startOfWeek = moment(from).startOf('week')
    const endOfWeek = moment(from).endOf('week')
    const newFilters: Filters = { ...searchToFilters(location.search), from: startOfWeek.tz("America/Argentina/Buenos_Aires").format(), to: endOfWeek.tz("America/Argentina/Buenos_Aires").format() }
    history.push({ search: filtersToSearch(newFilters), state: location.state })
  }

  return <Button color="primary" variant="contained" onClick={() => changeDateInterval(props.from)}>{props.text}</Button>
}

export const searchToFilters = (search: string, defaultFilters: Filters = {}) => {
  const filters = { ...defaultFilters }
  new URLSearchParams(search).forEach((value, key) => {
    filters[key] = value
  })
  return filters
}

export const filtersToSearch = (filters: Filters): string => paramsToQueryString(filters)

// Returns the date in from filter. If the filter does not exists,
// it returns the current date
export const currentFromFilter = (): Moment => {
  return moment(searchToFilters(window.location.search)['from'])
}